export enum PaymentInterval {
	Month,
	Year,
	OneTime,
}

export const PaymentIntervalToActiveName: Record<PaymentInterval, string> = {
	[PaymentInterval.Month]: "Monthly",
	[PaymentInterval.Year]: "Yearly",
	[PaymentInterval.OneTime]: "One-Time",
};

export type PriceDescriptor = {
	id: string;
	cents: number;
};

export type StripePrices = Record<PaymentInterval, PriceDescriptor>;

export type PricesMap = Record<PaymentInterval, undefined>;

export type StripeTier = {
	activations: number;
	title: string;
	description: string;
	features: readonly string[];
	is_best_deal: boolean;
	prices: PricesMap;
};

export const DEV_STRIPE_TIERS: Record<string, StripeTier> = {
	prod_TrihB6bwfDU85I: {
		activations: 1,
		title: "sesh Pro",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Premium features plus API access and custom branding",
		features: [
			"Premium for 1 server",
			"API Access",
			"Custom Branding",
			"Priority Support",
		],
		is_best_deal: false,
	},
	prod_NC6XOKwwTNZ4GD: {
		activations: 1,
		title: "Premium",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Fully unlock sesh! Get repeating events, ",
		features: [
			"Repeating events",
			"Custom RSVP options",
			"Full GCal sync",
			"Unlimited server logs",
			"Unlimited event templates",
			"Close RSVPs early",
			"Priority support",
			"...and much more!",
		],
		is_best_deal: false,
	},
	prod_NC6iPVmx2SkRzQ: {
		activations: 3,
		title: "Premium Bundle",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Description goes here",
		features: [
			"Repeating events",
			"Custom RSVP options",
			"Full GCal sync",
			"Unlimited server logs",
			"Unlimited event templates",
			"Close RSVPs early",
			"Priority support",
			"...and much more!",
		],
		is_best_deal: true,
	},
} as const;

export const PRODUCTION_TIERS: Record<string, StripeTier> = {
	prod_TuH95vKgizbWdz: {
		activations: 1,
		title: "sesh Pro",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Premium features plus API access and custom branding",
		features: [
			"Premium for 1 server",
			"API Access",
			"Custom Branding",
			"Priority Support",
		],
		is_best_deal: false,
	},
	prod_NCrwD9OIe8q3ep: {
		activations: 1,
		title: "Premium",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Fully unlock sesh! Get repeating events, ",
		features: [
			"Repeating events",
			"Custom RSVP options",
			"Full GCal sync",
			"Unlimited server logs",
			"Unlimited event templates",
			"Priority support",
			"...and much more!",
		],
		is_best_deal: false,
	},
	prod_NCrxDlEHuriMPU: {
		activations: 3,
		title: "Premium Bundle",
		prices: {
			[PaymentInterval.Month]: undefined,
			[PaymentInterval.Year]: undefined,
			[PaymentInterval.OneTime]: undefined,
		},
		description: "Description goes here",
		features: [
			"Repeating events",
			"Custom RSVP options",
			"Full GCal sync",
			"Unlimited server logs",
			"Unlimited event templates",
			"Priority support",
			"...and much more!",
		],
		is_best_deal: true,
	},
} as const;

export type StripeProductId = keyof typeof DEV_STRIPE_TIERS;

export function getTiersWithoutPrices() {
	let is_on_production = false;
	if (typeof window !== "undefined") {
		// web
		is_on_production =
			window?.location?.host?.includes("sesh.fyi") &&
			!window?.location?.host?.includes("beta");
	} else if (
		process.env.NODE_ENV === "production" &&
		!process.env.SESH_IS_STAGING
	) {
		is_on_production = true;
	}

	return is_on_production ? PRODUCTION_TIERS : DEV_STRIPE_TIERS;
}

export function getRawProductWithoutPrices(product_id: string) {
	return getTiersWithoutPrices()[product_id as StripeProductId];
}

// Sesh Pro custom tier product ID

// const PRODUCTION_STRIPE = {};

export type SubscriptionChoice = {
	span: PaymentInterval;
	id: StripeProductId;
};

const SESH_PRO_PRODUCT_IDS = [	
	"prod_TrihB6bwfDU85I",
	"prod_TuH95vKgizbWdz",
];

export function isSeshProProduct(product_id: string): boolean {
	return SESH_PRO_PRODUCT_IDS.includes(product_id);
}

// Helper to determine if we're in dev mode (used by product ID helpers below)
function isDevMode(): boolean {
	if (typeof window !== "undefined") {
		// web
		return (
			!window?.location?.host?.includes("sesh.fyi") ||
			window?.location?.host?.includes("beta")
		);
	}
	return !(
		process.env.NODE_ENV === "production" && !process.env.SESH_IS_STAGING
	);
}

// sesh Pro addon product IDs (for bundle upgrades - adds Pro to existing bundle subscription)
const DEV_SESH_PRO_ADDON_PRODUCT_ID = "prod_TrihB6bwfDU85I"; // TODO: Replace with actual Stripe product ID
const PROD_SESH_PRO_ADDON_PRODUCT_ID = "prod_TuH95vKgizbWdz"; // TODO: Replace with actual Stripe product ID

export function getSeshProAddonProductId(): string {
	return isDevMode()
		? DEV_SESH_PRO_ADDON_PRODUCT_ID
		: PROD_SESH_PRO_ADDON_PRODUCT_ID;
}

export function isSeshProAddonProduct(product_id: string): boolean {
	return (
		product_id === DEV_SESH_PRO_ADDON_PRODUCT_ID ||
		product_id === PROD_SESH_PRO_ADDON_PRODUCT_ID
	);
}

// Upgrade discount coupons for one-time purchases ($150 per slot)
// Create these coupons in both Stripe test mode and live mode
const DEV_SESH_PRO_UPGRADE_COUPONS = {
	1: "UEDW7EUt", // TODO: Replace with actual Stripe coupon ID
	2: "vjwxfxb2", // TODO: Replace with actual Stripe coupon ID
	3: "dccOCpGx", // TODO: Replace with actual Stripe coupon ID
} as const;

const PROD_SESH_PRO_UPGRADE_COUPONS = {
	1: "8gEgDzFB", // TODO: Replace with actual Stripe coupon ID
	2: "dI0oT6ft", // TODO: Replace with actual Stripe coupon ID
	3: "wwKQIUxq", // TODO: Replace with actual Stripe coupon ID
} as const;

export function getSeshProUpgradeCoupon(slotCount: 1 | 2 | 3): string {
	const coupons = isDevMode()
		? DEV_SESH_PRO_UPGRADE_COUPONS
		: PROD_SESH_PRO_UPGRADE_COUPONS;
	return coupons[slotCount];
}

// Recurring subscription coupons for bundle Pro upgrades
// These coupons give a recurring discount equal to (slots * bundle_price / 3)
// For a $99.99 bundle: 1 slot = $33.33/cycle, 2 slots = $66.66/cycle
// Create these in Stripe as:
//   - duration: forever
//   - amount_off: 3333 (1 slot), 6666 (2 slots) in cents
//   - currency: usd
const DEV_BUNDLE_PRO_UPGRADE_COUPONS = {
	1: "IvHlH9S5", // $33.33 off per cycle
	2: "v2jl0det", // $66.66 off per cycle
} as const;

const PROD_BUNDLE_PRO_UPGRADE_COUPONS = {
	1: "QzlGxVy2", // $33.33 off per cycle
	2: "7bdLIwl4", // $66.66 off per cycle
} as const;

export function getBundleProUpgradeCoupon(slotCount: 1 | 2): string {
	const coupons = isDevMode()
		? DEV_BUNDLE_PRO_UPGRADE_COUPONS
		: PROD_BUNDLE_PRO_UPGRADE_COUPONS;
	return coupons[slotCount];
}

// Bundle product IDs (3-server bundles)
const DEV_BUNDLE_PRODUCT_ID = "prod_NC6iPVmx2SkRzQ"; // From DEV_STRIPE_TIERS
const PROD_BUNDLE_PRODUCT_ID = "prod_NCrxDlEHuriMPU"; // From PRODUCTION_TIERS

export function isBundleProduct(product_id: string): boolean {
	return (
		product_id === DEV_BUNDLE_PRODUCT_ID ||
		product_id === PROD_BUNDLE_PRODUCT_ID
	);
}

// Single Premium product IDs (1-server Premium, non-bundle)
const DEV_SINGLE_PREMIUM_PRODUCT_ID = "prod_NC6XOKwwTNZ4GD"; // From DEV_STRIPE_TIERS
const PROD_SINGLE_PREMIUM_PRODUCT_ID = "prod_NCrwD9OIe8q3ep"; // From PRODUCTION_TIERS

export function isSinglePremiumProduct(product_id: string): boolean {
	return (
		product_id === DEV_SINGLE_PREMIUM_PRODUCT_ID ||
		product_id === PROD_SINGLE_PREMIUM_PRODUCT_ID
	);
}

export function getBundleProductId(): string {
	return isDevMode() ? DEV_BUNDLE_PRODUCT_ID : PROD_BUNDLE_PRODUCT_ID;
}

// Explicit sesh Pro Stripe product IDs
const DEV_SESH_PRO_PRODUCT_ID = "prod_TrihB6bwfDU85I";
const PROD_SESH_PRO_PRODUCT_ID = "prod_TuH95vKgizbWdz"; // TODO: Replace with actual Stripe product ID

/**
 * Get the actual Stripe product ID for sesh Pro (not the "sesh_pro" identifier)
 */
export function getSeshProStripeProductId(): string {
	return isDevMode() ? DEV_SESH_PRO_PRODUCT_ID : PROD_SESH_PRO_PRODUCT_ID;
}
